import {
  Button,
  Header,
  SegmentedControl,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import { ProductStatus } from '@parallel-fluidics/shared/models/products';
import InventoryMetric from 'features/inventory/components/InventoryMetric';
import ProductFormModal from 'features/products/components/ProductFormModal';
import ProductStatusBadge from 'features/products/components/ProductStatusBadge';
import useGetProductVersion from 'features/products/hooks/useGetProductVersion';
import useProductsQuery from 'features/products/hooks/useProductsQuery';
import useDebouncedValue from 'hooks/useDebouncedValue';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const statusOptions = [
  { text: 'All', id: 'all' },
  { text: 'Active', id: ProductStatus.ACTIVE },
  { text: 'Draft', id: ProductStatus.DRAFT },
  { text: 'Retired', id: ProductStatus.RETIRED },
];

function ActiveVersionValue({
  productDocId,
  versionId = null,
  children,
}) {
  const { data: version, isLoading } = useGetProductVersion(productDocId, versionId);

  if (!versionId) return '-';
  if (isLoading) return 'Loading...';
  if (!version) return '-';

  return children(version);
}

ActiveVersionValue.propTypes = {
  productDocId: PropTypes.string.isRequired,
  versionId: PropTypes.string,
  children: PropTypes.func.isRequired,
};

export default function ProductsPage() {
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');
  const [status, setStatus] = useState('all');
  const [openProductModal, setOpenProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const debouncedFilterText = useDebouncedValue(filterText);

  const {
    CustomPagination,
    isFetchingNextPage,
    pageData,
    refetch,
  } = useProductsQuery({
    filterText: debouncedFilterText,
    status,
  });

  const columnDefinitions = useMemo(
    () => [
      {
        id: 'sku',
        header: 'SKU',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <Button variant="inline-link" onClick={() => navigate(`/products/${item.id}`)}>
            {item.sku}
          </Button>
        ),
      },
      {
        id: 'activeVersion',
        header: 'Active Version',
        cell: (item) => item.activeVersionId || '-',
      },
      {
        id: 'pPartId',
        header: 'Project Part',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <ActiveVersionValue productDocId={item.id} versionId={item.activeVersionId}>
            {(version) => [version.projectId, version.pPartId].filter(Boolean).join('-') || '-'}
          </ActiveVersionValue>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        cell: (item) => item.name,
      },
      {
        id: 'status',
        header: 'Status',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => <ProductStatusBadge status={item.status} />,
      },
      {
        id: 'onHand',
        header: 'On Hand',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <InventoryMetric productDocId={item.id} versionId={item.activeVersionId} link />
        ),
      },
      {
        id: 'unitPrice',
        header: 'Unit Price',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <ActiveVersionValue productDocId={item.id} versionId={item.activeVersionId}>
            {(version) => (typeof version.unitPrice === 'number'
              ? `$${version.unitPrice.toFixed(2)}`
              : '-')}
          </ActiveVersionValue>
        ),
      },
      {
        id: 'leadtime',
        header: 'Lead Time',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <ActiveVersionValue productDocId={item.id} versionId={item.activeVersionId}>
            {(version) => (version.leadtimeDays ? `${version.leadtimeDays} days` : '-')}
          </ActiveVersionValue>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (
          <Button
            variant="normal"
            onClick={() => {
              setEditProduct(item);
              setOpenProductModal(true);
            }}
          >
            Edit
          </Button>
        ),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        cell: (item) => item.updatedAt?.toDate?.().toLocaleString() || '-',
      },
    ],
    [navigate],
  );

  return (
    <>
      <Table
        header={(
          <Header
            variant="awsui-h1-sticky"
            actions={(
              <Button
                iconName="add-plus"
                variant="primary"
                onClick={() => {
                  setEditProduct(null);
                  setOpenProductModal(true);
                }}
              >
                Create product
              </Button>
            )}
          >
            Products
          </Header>
        )}
        filter={(
          <div style={{ display: 'grid', gap: 12 }}>
            <TextFilter
              filteringPlaceholder="Find products by SKU"
              filteringText={filterText}
              onChange={({ detail }) => setFilterText(detail.filteringText)}
            />
            <SegmentedControl
              selectedId={status}
              options={statusOptions}
              onChange={({ detail }) => setStatus(detail.selectedId)}
            />
          </div>
        )}
        variant="full-page"
        stickyHeader
        resizableColumns
        wrapLines
        loading={isFetchingNextPage}
        items={pageData}
        columnDefinitions={columnDefinitions}
        pagination={<CustomPagination />}
        empty={<div>No products</div>}
      />

      {openProductModal && (
        <ProductFormModal
          open={openProductModal}
          isEditing={Boolean(editProduct)}
          initProduct={editProduct}
          onClose={() => {
            setOpenProductModal(false);
            setEditProduct(null);
          }}
          onSuccess={(productDocId) => {
            refetch();
            if (!editProduct && productDocId) {
              navigate(`/products/${productDocId}`);
            }
          }}
        />
      )}
    </>
  );
}
